/**
 * Holds the selected organisation, and makes every request carry it.
 *
 * ── WHY THE SELECTION LIVES IN A MODULE VARIABLE AND NOT ONLY IN STATE ───────
 * `lib/api/client.ts` builds every request's headers and is not a React component, so it cannot
 * read context. The selection is therefore mirrored into a module-scoped variable that the client
 * reads — one writer (this provider), one reader (the client), and the header is set in the one
 * place every request already passes through rather than at ~200 call sites.
 *
 * ── AND WHY IT IS PERSISTED ─────────────────────────────────────────────────
 * A reload must not silently drop you back into your own organisation while the screen still
 * shows a descendant's fleet. `localStorage`, read once at startup, and the SERVER validates it
 * on every request anyway — an id outside the reach is ignored and the caller falls back to their
 * own organisation, so a stale entry is harmless rather than an error.
 *
 * ── CHANGING IT RESETS EVERYTHING BENEATH IT ────────────────────────────────
 * Site ids do not survive an organisation change any more than plant ids survive a site change,
 * and the whole query cache is keyed on data that has just been re-scoped. So a switch clears the
 * cache and the stored site. Anything less shows one organisation's sites under another's name
 * for as long as the stale entries live.
 *
 * ── AND WHY THE CONTROL ITSELF IS GATED ON `tenants.edit` ────────────────────
 * Measured 2026-09-23: signing in as a KSolar Field Engineer holding only `alarms.triage`,
 * `findings.manage` and `users.read` still showed the header organisation switcher listing
 * KSolar and its sub-organisations. Nothing checked a permission — `visible` tested only
 * `organisations.length > 1`, and the list arrives from `/me` with no gate of its own.
 *
 * This was NOT a data leak. `lib/org-reach.ts` (lumen-be) already widens every ordinary read
 * downward through the whole subtree with no switch involved at all — `modules/sites/service.ts`
 * states the rule its `Ctx.reach` field is for: *"That organisation PLUS EVERY DESCENDANT — the
 * READ set (`ORG-REACH.md`). `orgId` is untouched and still means exactly one organisation: it
 * stays what every write and every audit row written here is stamped with. Only the reads
 * widen."* A field engineer already sees a descendant's sites, alarms and telemetry in his
 * ordinary lists, switcher or not.
 *
 * What the switcher adds on top of that read set is a WRITE TARGET — the file's opening section
 * — and deciding where a create lands is an administrative act, not a viewing one. It is gated on
 * `tenants.edit`, the same slug that gates the Organisation screen (`nav.ts`) and every
 * organisation edit, because this control decides the identical thing from the header instead of
 * a rail entry.
 *
 * `e3a427c` ("the rail, the filters and the cache follow what you hold") added `needs:` to every
 * rail entry for exactly this reason and never reached the header — this is that pass, finished.
 *
 * ── AND WHY LOSING THE PERMISSION CANNOT STRAND ANYONE ───────────────────────
 * `78bd790` exists because an earlier version of this control could switch someone into an
 * organisation and then take away their only way back out. Hiding the button the moment
 * `tenants.edit` is gone would reopen exactly that trap for a caller already sitting in a
 * descendant when the gate closes — the control disappears, but nothing moves the actual
 * selection, because `lib/org-reach.ts`'s reach walk carries no permission check of its own and
 * keeps honouring whatever was last asked. See the effect beside `visible` below: it is the
 * client doing, for this one case, what the server's own "an unreachable id falls back to home"
 * rule does not cover.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMe } from '@/lib/api/me';
import { setRequestOrganisation } from '@/lib/api/client';
import { PRODUCT } from '@/product/profile';
import { OrgContext, type OrgContextValue, type OrgNode } from './org-context-value';
import { orgSwitcherVisible, orgSwitchStrandTarget, productDefaultOrg } from './org-context-logic';

/* Per product (`profile.ts` → `storagePrefix`): Thunder keeps `lumen.organisation`. */
const STORAGE_KEY = `${PRODUCT.storagePrefix}.organisation`;

function stored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    /* Private windows and blocked site data throw on access. A selection nobody can persist is
       still a selection for this tab. */
    return null;
  }
}

export function OrgProvider({ children }: { children: ReactNode }): ReactNode {
  const me = useMe();
  const qc = useQueryClient();
  /*
   * SEEDED DURING THE FIRST RENDER, not in an effect — and that is the fix to a second bug
   * measured the same way.
   *
   * `client.ts` initialises its module variable to `null`, and the effect below is what used to
   * set it. Effects run AFTER the subtree mounts, so every request the first render fired —
   * `/me`, `/sites`, the alarm and findings badges — went out with no `X-Lumen-Organisation`
   * header at all and came back scoped to the caller's HOME organisation. A reload while a
   * descendant was selected therefore showed the parent's fleet under the descendant's name,
   * and the stored selection only took effect from the second fetch onwards.
   *
   * The initialiser runs before this provider returns, which is before any child mounts and so
   * before any query fires. The effect stays, because it is what carries a LATER change; this
   * only covers the first one.
   */
  const [asked, setAsked] = useState<string | null>(() => {
    const seed = stored();
    setRequestOrganisation(seed);
    return seed;
  });

  const reach = me.data?.reach;
  const organisations: OrgNode[] = useMemo(() => reach?.organisations ?? [], [reach]);

  /*
   * The server's answer is the authority. It already ignored an unreachable id and fell back, so
   * `reach.selected` is what this request was actually scoped to — believing our own `asked` over
   * it would let the screen claim an organisation the data does not come from.
   */
  const selectedId = reach?.selected ?? reach?.home ?? '';
  const homeId = reach?.home ?? '';

  /*
   * THE ONE PERMISSION THAT MAKES THIS CONTROL AN OFFER RATHER THAN A LEAK OF CHROME.
   * `tenants.edit` — the same slug `nav.ts` gates the Organisation screen on. See the file doc
   * above for why a write target is an administrative fact and a read is not, and
   * `org-context-logic.ts` for the two decisions this feeds.
   */
  const canSwitch = me.data?.permissions.includes('tenants.edit') ?? false;

  useEffect(() => {
    setRequestOrganisation(asked);
  }, [asked]);

  const select = useCallback(
    (id: string) => {
      if (id === selectedId) return;
      setAsked(id);
      setRequestOrganisation(id);
      try {
        localStorage.setItem(STORAGE_KEY, id);
      } catch {
        /* See `stored` — an unpersisted selection still holds for this tab. */
      }
      /*
       * EVERYTHING CACHED WAS SCOPED TO THE OLD ORGANISATION, including the site the header is
       * pointing at. Blunt and correct: the alternative is enumerating which keys survive a
       * re-scope, which is a list that silently goes stale as screens are added.
       *
       * ───────────────────────────────────────────────────────────────────────
       * THIS WAS `qc.clear()`, AND `clear()` DOES NOT REFETCH — 2026-09-18
       * ───────────────────────────────────────────────────────────────────────
       *
       * `clear()` empties the cache and notifies nobody. Mounted observers keep the data they
       * last rendered and sit on it, so switching organisation left every list on every screen
       * showing the organisation you had just left, until something unrelated happened to
       * trigger a fetch. That is the whole of the reported bug — *"when I change these filters
       * the page should re-render, currently I need to reload it"* — and a reload worked because
       * a reload is the only thing that refetched.
       *
       * MEASURED, not reasoned: with a request log on `/sites`, a full session that signed in,
       * opened a site and switched organisation fired exactly ONE `/sites` request, at startup.
       * Zero after the switch. The site selector still listed `Jamshoro` while the header named
       * a sub-organisation that owns no sites at all.
       *
       * `resetQueries()` is the primitive that matches the intent: it discards the data AND
       * refetches everything active. `invalidateQueries()` would also refetch but leaves the old
       * rows on screen while it does, which on a re-scope means rendering one organisation's
       * fleet under another's name for the length of a request — the exact thing this block
       * exists to prevent. Resetting shows skeletons instead, which is the honest state.
       */
      try {
        /* `site-context.tsx`'s own key. Named here rather than imported because that module
           exports a provider, not a constant, and reaching into it for one string would couple
           two contexts that are otherwise independent. If it ever changes, this line is the one
           that has to change with it — which is why it says so. */
        localStorage.removeItem('lumen.selectedSiteId');
      } catch {
        /* ignored, as above */
      }
      void qc.resetQueries();
    },
    [qc, selectedId],
  );

  /*
   * A CALLER WHO LOSES `tenants.edit` MID-SESSION MUST NOT STAY WHERE THE CONTROL LEFT THEM.
   *
   * `visible` below hides the button the instant `canSwitch` goes false, but hiding the button
   * does not by itself move anyone — the header still carries whatever `asked` last pointed at.
   * `orgSwitchStrandTarget` (`org-context-logic.ts`) names the target to walk back to, or `null`
   * when nothing needs to move; when it names one, this calls the same `select()` a person would
   * have used, which clears the stored site and the query cache along with it, not just the label
   * on the button. See that function's doc for why the server's own fallback does not already
   * cover this, and the `78bd790` strand it closes.
   */
  useEffect(() => {
    const target = orgSwitchStrandTarget({ loading: me.isLoading, canSwitch, selectedId, homeId });
    if (target !== null) select(target);
  }, [me.isLoading, canSwitch, selectedId, homeId, select]);

  /*
   * THE PRODUCT'S OWN ORGANISATION, the first time this product is opened. A Thunder administrator
   * opening the Cratus console lands in Cratus — not in their home organisation, whose fleet is
   * not Cratus's, and not in whatever another product last left selected (which is why the key is
   * per product). Through `select()`, so it is persisted and re-scopes like a person's choice; and
   * only while nothing has been chosen, so a person's choice always wins.
   */
  useEffect(() => {
    const target = productDefaultOrg({
      loading: me.isLoading,
      asked,
      canSwitch,
      slug: PRODUCT.organisationSlug,
      organisations,
      selectedId,
    });
    if (target !== null) select(target);
  }, [me.isLoading, asked, canSwitch, organisations, selectedId, select]);

  const value = useMemo<OrgContextValue>(
    () => ({
      organisations,
      selectedId,
      selected: organisations.find((o) => o.id === selectedId) ?? null,
      homeId,
      /* `orgSwitcherVisible` (`org-context-logic.ts`) — hidden, not disabled, same as before. */
      visible: orgSwitcherVisible(canSwitch, organisations.length),
      loading: me.isLoading,
      select,
    }),
    [organisations, selectedId, homeId, canSwitch, me.isLoading, select],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
